import { isEmpty } from 'lodash';

import { NULL_VALUE } from '@constants';
import {
  HAS_QUICK,
  IAccount,
  QUICK_ORG_IDS_PREFERENCE_KEY,
  QUICK_SITE_IDS_PREFERENCE_KEY,
} from '@share/common-types';

const getPreferenceValue = (account: IAccount | null | undefined, key: string, defaultValue?: string): string => {
  if (!isEmpty(account?.preferences)) {
    const pref = account?.preferences.find((p) => p.key === key);
    if (!isEmpty(pref?.value)) {
      return pref?.value as string;
    }
  }
  return !isEmpty(defaultValue) ? (defaultValue as string) : NULL_VALUE;
};

/** Parses comma-separated quick org/site id preference strings (same rules as former search helper). */
export const parseQuickIdListPreference = (val?: string): number[] =>
  val?.split(',').map((v) => Number(v.trim())).filter(Boolean) ?? [];

export const getQuickOrgSiteIdsFromAccount = (account: IAccount): { orgIds: number[]; siteIds: number[] } => {
  const orgStr = getPreferenceValue(account, QUICK_ORG_IDS_PREFERENCE_KEY, '');
  const siteStr = getPreferenceValue(account, QUICK_SITE_IDS_PREFERENCE_KEY, '');
  return {
    orgIds: parseQuickIdListPreference(orgStr),
    siteIds: parseQuickIdListPreference(siteStr),
  };
};

/**
 * Quick is active when the preference is on and either both lists are empty (all orgs/sites),
 * or the current org or site appears in the configured lists.
 * When lists are restricted but org/site are unknown, returns false.
 */
export const getEffectiveHasQuick = (
  quickPreferenceEnabled: boolean,
  orgIds: number[],
  siteIds: number[],
  organizationId?: number | null,
  siteId?: number | null,
): boolean => {
  if (!quickPreferenceEnabled) {
    return false;
  }
  if (!orgIds.length && !siteIds.length) {
    return true;
  }
  const orgMatch = organizationId != null && orgIds.includes(organizationId);
  const siteMatch = siteId != null && siteIds.includes(siteId);
  return orgMatch || siteMatch;
};

/** Used when building the account before navigation context exists (org/site unknown). */
export const getHasQuickFromAccountPreferences = (account: IAccount): boolean => {
  const quickPreferenceEnabled = JSON.parse(getPreferenceValue(account, HAS_QUICK, 'false'));
  const { orgIds, siteIds } = getQuickOrgSiteIdsFromAccount(account);
  return getEffectiveHasQuick(quickPreferenceEnabled, orgIds, siteIds, undefined, undefined);
};

/** Resolves hasQuick once menu response provides siteId and organizationId. */
export const resolveHasQuickForNavigation = (
  account: IAccount,
  organizationId?: number | null,
  siteId?: number | null,
): boolean => {
  const quickPreferenceEnabled = JSON.parse(getPreferenceValue(account, HAS_QUICK, 'false'));
  const { orgIds, siteIds } = getQuickOrgSiteIdsFromAccount(account);
  return getEffectiveHasQuick(quickPreferenceEnabled, orgIds, siteIds, organizationId, siteId);
};
